import {
  UntrackedAssetCatalogApiClient,
  createUntrackedAssetCatalogApiClient,
} from '@peritasai/data-access';
import type {
  CreateUntrackedAssetCatalogDto,
  CreateUntrackedAssetCatalogWithTaxonomyDto,
  InstrumentTaxonomyClassDto,
  PaginatedUntrackedAssetCatalogResponseDto,
  UntrackedAssetCatalogDto,
  UntrackedAssetCatalogFilterParams,
  UntrackedAssetCatalogTaxonomyResponseDto,
  UpdateUntrackedAssetCatalogDto,
  UpdateUntrackedAssetCatalogTaxonomyDto,
} from '@peritasai/types';
import { UntrackedAssetCatalogEntity } from './untracked-asset-catalog.entity';

export interface IUntrackedAssetCatalogRepository {
  getUntrackedAssetCatalogPaginated(
    options: UntrackedAssetCatalogFilterParams
  ): Promise<PaginatedUntrackedAssetCatalogResponseDto & { items: UntrackedAssetCatalogEntity[] }>;
  createUntrackedAssetCatalog(
    dto: CreateUntrackedAssetCatalogDto
  ): Promise<UntrackedAssetCatalogEntity>;
  updateUntrackedAssetCatalog(
    id: string,
    dto: UpdateUntrackedAssetCatalogDto
  ): Promise<UntrackedAssetCatalogEntity>;
  updateTaxonomyLink(
    catalogItemId: string,
    dto: UpdateUntrackedAssetCatalogTaxonomyDto
  ): Promise<UntrackedAssetCatalogTaxonomyResponseDto>;
  createWithTaxonomy(
    dto: CreateUntrackedAssetCatalogWithTaxonomyDto
  ): Promise<{ entity: UntrackedAssetCatalogEntity; taxonomyError?: string }>;
  getInstrumentTaxonomyClasses(): Promise<InstrumentTaxonomyClassDto[]>;
  fetchClassImage(imageId: number): Promise<Blob>;
}

export class UntrackedAssetCatalogRepository implements IUntrackedAssetCatalogRepository {
  constructor(private readonly apiClient: UntrackedAssetCatalogApiClient) {}

  async getUntrackedAssetCatalogPaginated(
    options: UntrackedAssetCatalogFilterParams = {}
  ): Promise<PaginatedUntrackedAssetCatalogResponseDto & { items: UntrackedAssetCatalogEntity[] }> {
    const response = await this.apiClient.getUntrackedAssetCatalog(options);
    return {
      ...response,
      items: response.data.map((dto) => this.toEntity(dto)),
    };
  }

  async createUntrackedAssetCatalog(
    dto: CreateUntrackedAssetCatalogDto
  ): Promise<UntrackedAssetCatalogEntity> {
    const result = await this.apiClient.createUntrackedAssetCatalog(dto);
    return this.toEntity(result);
  }

  async updateUntrackedAssetCatalog(
    id: string,
    dto: UpdateUntrackedAssetCatalogDto
  ): Promise<UntrackedAssetCatalogEntity> {
    const result = await this.apiClient.updateUntrackedAssetCatalog(id, dto);
    return this.toEntity(result);
  }

  async updateTaxonomyLink(
    catalogItemId: string,
    dto: UpdateUntrackedAssetCatalogTaxonomyDto
  ): Promise<UntrackedAssetCatalogTaxonomyResponseDto> {
    return this.apiClient.updateTaxonomyLink(catalogItemId, dto);
  }

  async createWithTaxonomy(
    dto: CreateUntrackedAssetCatalogWithTaxonomyDto
  ): Promise<{ entity: UntrackedAssetCatalogEntity; taxonomyError?: string }> {
    const { instrumentTaxonomyClassId, ...baseDto } = dto;
    const createdDto = await this.apiClient.createUntrackedAssetCatalog(baseDto);

    if (instrumentTaxonomyClassId != null) {
      try {
        const taxonomyResponse = await this.apiClient.updateTaxonomyLink(createdDto.id, {
          instrumentTaxonomyClassId,
        });
        return {
          entity: this.toEntity({
            ...createdDto,
            instrumentTaxonomyClassId: taxonomyResponse.instrumentTaxonomyClassId,
            instrumentTaxonomyClassName: taxonomyResponse.instrumentTaxonomyClass?.name ?? null,
            instrumentTaxonomyCategoryName:
              taxonomyResponse.instrumentTaxonomyClass?.category ?? null,
          }),
        };
      } catch (err) {
        return {
          entity: this.toEntity(createdDto),
          taxonomyError: err instanceof Error ? err.message : 'Taxonomy assignment failed',
        };
      }
    }

    return { entity: this.toEntity(createdDto) };
  }

  async getInstrumentTaxonomyClasses(): Promise<InstrumentTaxonomyClassDto[]> {
    return this.apiClient.getInstrumentTaxonomyClasses();
  }

  async fetchClassImage(imageId: number): Promise<Blob> {
    return this.apiClient.fetchClassImage(imageId);
  }

  private toEntity(dto: UntrackedAssetCatalogDto): UntrackedAssetCatalogEntity {
    return new UntrackedAssetCatalogEntity(dto);
  }
}

export const untrackedAssetCatalogRepository = new UntrackedAssetCatalogRepository(
  createUntrackedAssetCatalogApiClient()
);
